import {
  ArrowDropDownRounded,
  ChevronRightRounded,
  DescriptionRounded,
  Logout,
  Menu as MenuIcon,
  Payments,
  PeopleAltRounded,
  Subscriptions,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import AppVersionBadge from "../components/tools/AppVersionBadge";
import { drawerWidth } from "../config/config";
import { useAuth } from "../data/providers/AuthProvider";
import { useUserData } from "../data/providers/UserDataProvider";
import { authManager } from "../data/managers/AuthManager";
import { getInitials } from "../utils/StringTool";

type AdminNavItem = {
  label: string;
  path: string;
  icon: React.ReactElement;
  section: string;
  title: string;
  subtitle: string;
};

const adminNavItems: AdminNavItem[] = [
  {
    label: "Usuarios",
    path: "/dashboard/admin/users",
    icon: <PeopleAltRounded />,
    section: "Gestión",
    title: "Usuarios",
    subtitle: "Listado general",
  },
  {
    label: "Planes",
    path: "/dashboard/admin/plans",
    icon: <Subscriptions />,
    section: "Gestión",
    title: "Planes",
    subtitle: "Capacidades y recursos",
  },
  {
    label: "Cuestionarios",
    path: "/dashboard/admin/questionnaires",
    icon: <DescriptionRounded />,
    section: "Cuestionarios",
    title: "Listado General",
    subtitle: "Editor y versiones",
  },
  {
    label: "Pagos",
    path: "/dashboard/admin/payments",
    icon: <Payments />,
    section: "Gestión",
    title: "Pagos",
    subtitle: "Registro y trazabilidad",
  },
];

const getAdminPageMeta = (pathname: string) => {
  const matched =
    adminNavItems.find(
      (item) => pathname === item.path || pathname.startsWith(`${item.path}/`),
    ) ?? adminNavItems[0];

  return matched;
};

const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const userData = useUserData();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [logoutOpen, setLogoutOpen] = useState(false);

  const userMenuOpen = Boolean(userMenuAnchor);
  const pageMeta = useMemo(
    () => getAdminPageMeta(location.pathname),
    [location.pathname],
  );

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    setLogoutOpen(false);
    await authManager.logout();
    navigate("/login", { replace: true });
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          px: 2.5,
          py: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: "#f7f9ff",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: `0 14px 26px ${alpha(theme.palette.primary.main, 0.08)}`,
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src="/assets/icons/logo.png"
            alt="Convisas"
            sx={{
              width: "88%",
              height: "88%",
              objectFit: "contain",
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ letterSpacing: 0.2, lineHeight: 1.1 }}
          >
            CONVISAS
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin Dashboard
          </Typography>
        </Box>
      </Box>

      <Divider />

      <List sx={{ px: 1.25, py: 1.5, flex: 1 }}>
        {adminNavItems.map((item) => {
          const isSelected =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={isMobile ? () => setMobileOpen(false) : undefined}
              selected={isSelected}
              sx={{
                mx: 0.5,
                my: 0.5,
                borderRadius: 2.5,
                minHeight: 48,
                px: 1.5,
                color: isSelected ? theme.palette.primary.main : "text.primary",
                bgcolor: isSelected
                  ? alpha(theme.palette.primary.main, 0.12)
                  : "transparent",
                boxShadow: isSelected
                  ? `inset 3px 0 0 ${theme.palette.primary.main}`
                  : "none",
                transition: "all 0.18s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: isSelected ? theme.palette.primary.main : "text.secondary",
                }}
              >
                {React.cloneElement(item.icon as React.ReactElement<any>, {
                  sx: { fontSize: 20 },
                })}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: "0.96rem",
                      fontWeight: isSelected ? 700 : 500,
                    },
                  },
                  secondary: {
                    sx: {
                      fontSize: "0.78rem",
                    },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <AppVersionBadge />
      </Box>
    </Box>
  );

  const userMenu = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 240,
          borderRadius: 3,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)",
          border: "1px solid rgba(15, 23, 42, 0.08)",
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {userData?.data?.fullName || "Admin"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.email || user?.phoneNumber || "Administrador"}
        </Typography>
      </Box>
      <Divider />
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          setLogoutOpen(true);
        }}
        sx={{ py: 1.2 }}
      >
        <ListItemIcon sx={{ minWidth: 34 }}>
          <Logout fontSize="small" />
        </ListItemIcon>
        Cerrar sesión
      </MenuItem>
    </Menu>
  );

  const sidebar = (
    <Box
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        borderRight: "1px solid rgba(15, 23, 42, 0.08)",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {drawerContent}
    </Box>
  );

  const desktopHeader = (
    <Box
      sx={{
        minHeight: 72,
        px: { xs: 2, md: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        bgcolor: "#ffffff",
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
        <Box>
          <Breadcrumbs
            separator={<ChevronRightRounded fontSize="small" />}
            aria-label="admin breadcrumb"
            sx={{
              "& .MuiBreadcrumbs-separator": {
                color: "text.disabled",
              },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {pageMeta.section}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {pageMeta.title}
            </Typography>
          </Breadcrumbs>
          <Typography variant="caption" color="text.secondary">
            {pageMeta.subtitle}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={handleUserMenuOpen}
        sx={{
          cursor: "pointer",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <Avatar
          src={user?.photoURL || ""}
          alt={userData?.data?.fullName || "Admin"}
          sx={{
            width: 34,
            height: 34,
            bgcolor: theme.palette.primary.main,
            fontWeight: 700,
          }}
        >
          {!user?.photoURL && getInitials(userData?.data?.fullName)}
        </Avatar>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography variant="body2" fontWeight={700} lineHeight={1.1}>
            {userData?.data?.fullName || "Admin"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin
          </Typography>
        </Box>
        <ArrowDropDownRounded sx={{ color: "text.secondary" }} />
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#f5f7fb",
      }}
    >
      {!isMobile && sidebar}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f5f7fb",
        }}
      >
        {desktopHeader}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "#ffffff",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {userMenu}

      <ConfirmationDialog
        open={logoutOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => void handleLogout()}
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
      />
    </Box>
  );
};

export default AdminLayout;
